(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PrintBuilderLogic = api;
})(typeof self !== 'undefined' ? self : this, function () {
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

  function maxDesigns(quantity, rules){
    if(!quantity) return 0;
    if(quantity < rules.minPerDesignMulti) return 1;
    return Math.min(rules.maxDesigns, Math.floor(quantity / rules.minPerDesignMulti));
  }

  function split(quantity, count, rules){
    const total = quantity || 0;
    if(!count) return [];
    if(count === 1) return [total];
    const per = Math.max(rules.minPerDesignMulti, Math.floor(total / count));
    const allocs = [];
    let rem = total;
    for(let i = 0; i < count; i++){
      if(i === count - 1){ allocs.push(rem); } else { allocs.push(per); rem -= per; }
    }
    return allocs;
  }

  function allocProblems(allocs, quantity, rules){
    if(!allocs || !allocs.length || !quantity) return [];
    const problems = [], total = allocs.reduce((s, a) => s + (a || 0), 0);
    if(allocs.length > 1) allocs.forEach((a, index) => { if(a < rules.minPerDesignMulti) problems.push({ code: 'below_min', index, min: rules.minPerDesignMulti }); });
    else if(allocs[0] < rules.minSingle) problems.push({ code: 'below_single_min', min: rules.minSingle });
    if(total !== quantity) problems.push({ code: 'sum', expected: quantity, actual: total });
    return problems;
  }

  function designLimit(quantity, wanted, rules){
    const allowed = maxDesigns(quantity, rules);
    const cap = Math.min(Math.max(wanted, allowed + 1), rules.maxDesigns);
    return { allowed, cap, need: cap * rules.minPerDesignMulti, overMax: wanted > rules.maxDesigns };
  }

  function extensionOf(name){
    const parts = String(name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  function fileProblem({ type, name }){
    const mime = String(type || '').toLowerCase();
    const ext = extensionOf(name);
    if(mime && mime !== 'application/octet-stream' ? ACCEPTED_TYPES.includes(mime) : ACCEPTED_EXTENSIONS.includes(ext)) return null;
    if(mime === 'image/tiff' || ext === 'tif' || ext === 'tiff') return 'tiff';
    if(mime === 'application/pdf' || ext === 'pdf') return 'pdf';
    if(mime === 'image/heic' || mime === 'image/heif' || ext === 'heic' || ext === 'heif') return 'heic';
    return 'unsupported';
  }

  function printDpi({ width, height }, { shortIn, longIn }){
    if(!width || !height) return 0;
    const px = [width, height].sort((a, b) => a - b);
    return Math.round(Math.min(px[0] / shortIn, px[1] / longIn));
  }

  function isLowResolution(dpi, threshold){
    return dpi > 0 && dpi < threshold;
  }

  function rotatedSize(width, height, rotation){
    return rotation === 90 || rotation === 270 ? { width: height, height: width } : { width, height };
  }

  function fullFrame({ imageWidth, imageHeight, rotation, aspect }){
    const size = rotatedSize(imageWidth, imageHeight, rotation);
    if(size.width / size.height > aspect) return { w: Math.min(1, size.height * aspect / size.width), h: 1 };
    return { w: 1, h: Math.min(1, size.width / aspect / size.height) };
  }

  function clampCenter({ x, y, w, h }){
    return {
      x: Math.min(Math.max(x, 0), 1 - w),
      y: Math.min(Math.max(y, 0), 1 - h),
      w,
      h
    };
  }

  function centerOf({ x, y, w, h }){
    return { centerX: x + w / 2, centerY: y + h / 2 };
  }

  function cropRect(params){
    const zoom = params.zoom > 1 ? params.zoom : 1;
    const frame = fullFrame(params);
    const w = frame.w / zoom, h = frame.h / zoom;
    return clampCenter({ x: params.centerX - w / 2, y: params.centerY - h / 2, w, h });
  }

  function zoomOf(rect, geometry){
    return fullFrame(geometry).w / rect.w;
  }

  function cropForMode(mode, params){
    if(mode === 'crop') return cropRect(params);
    if(mode === 'fit' || mode === 'border') return { x: 0, y: 0, w: 1, h: 1 };
    throw new Error('Unknown fit mode: ' + mode);
  }

  function panCenter(rect, dx, dy){
    return centerOf(clampCenter({ x: rect.x - dx * rect.w, y: rect.y - dy * rect.h, w: rect.w, h: rect.h }));
  }

  function stretchPercent({ imageWidth, imageHeight, rotation, aspect }){
    const size = rotatedSize(imageWidth, imageHeight, rotation);
    const ratio = size.width / size.height / aspect;
    return Math.round((Math.max(ratio, 1 / ratio) - 1) * 100);
  }

  function whiteFrame({ imageWidth, imageHeight, rotation, aspect }){
    const size = rotatedSize(imageWidth, imageHeight, rotation);
    if(size.width / size.height > aspect) return { w: 1, h: aspect * size.height / size.width };
    return { w: size.width / size.height / aspect, h: 1 };
  }

  function coverPixels(mode, params){
    const size = rotatedSize(params.imageWidth, params.imageHeight, params.rotation);
    if(mode === 'border'){
      const box = whiteFrame(params);
      return { width: size.width / box.w, height: size.height / box.h };
    }
    const rect = mode === 'fit' ? fullFrame(params) : cropForMode(mode, params);
    return { width: rect.w * size.width, height: rect.h * size.height };
  }

  function stagePlacement(mode, params){
    let box;
    if(mode === 'crop'){
      const rect = cropRect(params);
      box = { left: -rect.x / rect.w, top: -rect.y / rect.h, width: 1 / rect.w, height: 1 / rect.h };
    } else if(mode === 'fit'){
      box = { left: 0, top: 0, width: 1, height: 1 };
    } else if(mode === 'border'){
      const frame = whiteFrame(params);
      box = { left: (1 - frame.w) / 2, top: (1 - frame.h) / 2, width: frame.w, height: frame.h };
    } else {
      throw new Error('Unknown fit mode: ' + mode);
    }
    const turned = params.rotation === 90 || params.rotation === 270;
    const width = turned ? box.height / params.aspect : box.width;
    const height = turned ? box.width * params.aspect : box.height;
    return {
      left: box.left + (box.width - width) / 2,
      top: box.top + (box.height - height) / 2,
      width,
      height,
      rotation: params.rotation
    };
  }

  function blockedReasons({ quantity, designs, variantFound, rules }){
    const list = designs || [];
    const printable = list.filter(d => d.status !== 'refused');
    const hasStatus = status => list.some(d => d.status === status);
    const reasons = [];
    if(!quantity) reasons.push('no_quantity');
    if(!list.length) reasons.push('no_designs');
    if(quantity && printable.length > maxDesigns(quantity, rules)) reasons.push('too_many_designs');
    if(hasStatus('uploading')) reasons.push('uploading');
    if(hasStatus('failed')) reasons.push('upload_failed');
    if(hasStatus('refused')) reasons.push('refused_file');
    if(hasStatus('unchecked')) reasons.push('unchecked');
    if(allocProblems(printable.map(d => d.alloc), quantity, rules).length) reasons.push('allocation');
    if(variantFound === false) reasons.push('no_variant');
    return reasons;
  }

  return {
    maxDesigns,
    split,
    allocProblems,
    designLimit,
    fileProblem,
    printDpi,
    isLowResolution,
    rotatedSize,
    cropRect,
    clampCenter,
    centerOf,
    zoomOf,
    cropForMode,
    blockedReasons,
    panCenter,
    stretchPercent,
    coverPixels,
    stagePlacement
  };
});
